import { AccountInfo } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useCreateUser } from "@coworker/apprestructured/src/shared/hooks/users/useCreateUser";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import ChevronDownIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronDownIconPath";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import { Trans, useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import FullScreenPopup from "../../../layout/components/FullScreenPopup/FullScreenPopup";
import { SimpleHeader } from "../../../layout/components/SimpleHeader/SimpleHeader";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import { SuperUsersListModal } from "../../../shared/components/SuperUsersListModal/SuperUsersListModal";
import { UserRoles } from "../../../shared/enums/user/userRoles";
import { SimpleLoadingBall } from "../../../shared/simple/SimpleLoading/SimpleLoading";
import { ATKOMST_TEAMS_EDITOR_PERMISSION } from "../../../shared/types/atkomstPermissions";
import { normalizeFixaTeamStoreId } from "../../../shared/types/store";
import { Team, TeamStatus } from "../../../shared/types/team";
import { FixaFormField } from "../../../shared/wrappers/FixaFormField/FixaFormField";
import { FixaInputField } from "../../../shared/wrappers/FixaInputField/FixaInputField";
import { FixaText } from "../../../shared/wrappers/FixaText/FixaText";
import { useAtkomstTeamsByStore } from "../../hooks/useAtkomstTeamsByStore";
import useUserAtkomstPermissions from "../../hooks/useUserAtkomstPermissions";
import { TeamSelectionMessage } from "../TeamSelection/Message/TeamSelectionMessage";
import { TeamsModal } from "../TeamSelection/TeamsModal/TeamsModal";
import styles from "./newuserteampage.module.css";

interface NewUserTeamPageState {
  storeId: string;
  storeName: string;
  nodeType?: "STO" | "RU";
}

export const NewUserTeamPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as NewUserTeamPageState | null;
  const storeId = state?.storeId ?? "";
  const storeName = state?.storeName ?? "";
  const nodeType = state?.nodeType ?? "STO";
  // Åtkomst only knows the base store node, so the permissions check and the
  // super-users list use the normalized id (e.g. "012#fixa-team" → "012").
  // The teams fetch keeps the selected id so a virtual store is not resolved to
  // the real store's teams.
  const normalizedStoreId = normalizeFixaTeamStoreId(storeId);

  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const msalAccount = useAccount(accounts[0] || {}) as AccountInfo;
  const queryClient = useQueryClient();
  const { setLoggedInUser } = useLoggedInUser();
  const { toggleModal } = useContext(ModalContext);

  const { data: teamsData, isLoading: isTeamsLoading } = useAtkomstTeamsByStore(
    storeId || null
  );
  const { data: atkomstPermissions, isLoading: isPermissionsLoading } =
    useUserAtkomstPermissions(normalizedStoreId);
  const {
    mutateAsync: createUserMutation,
    isPending: isCreatingUser,
    isError: isCreateUserError,
  } = useCreateUser();

  const roleId = (() => {
    if (!atkomstPermissions) return UserRoles.COWORKER;
    if (
      nodeType === "RU" &&
      atkomstPermissions.includes(ATKOMST_TEAMS_EDITOR_PERMISSION)
    )
      return UserRoles.COUNTRY_SUPERUSER;
    if (atkomstPermissions.includes(ATKOMST_TEAMS_EDITOR_PERMISSION))
      return UserRoles.STOREOWNER;
    return UserRoles.COWORKER;
  })();

  const isLoading = isTeamsLoading || isPermissionsLoading;

  let email =
    msalAccount?.idTokenClaims?.preferred_username ??
    msalAccount?.username ??
    "";
  if (email?.split("@")[1]?.toLowerCase() === "ikea.com") {
    email = email.replace("ikea", "ingka.ikea");
  }
  const uid = msalAccount?.idTokenClaims?.oid ?? "";

  const defaultTeamOption = {
    id: "defaultOption",
    name: "",
    is_product_quality: false,
    status: TeamStatus.ACTIVE,
  } as Team;

  const [selectedTeam, setSelectedTeam] = useState<Team>(defaultTeamOption);
  const [shouldValidate, setShouldValidate] = useState(false);

  // Redirect to home if no store was passed (e.g. user refreshed the page)
  useEffect(() => {
    if (!storeId) {
      navigate("/home");
    }
  }, [storeId, navigate]);

  const activeTeams =
    (teamsData?.filter(
      (team) => team.status === TeamStatus.ACTIVE
    ) as Team[]) ?? [];
  const hasTeams = activeTeams.length > 0;

  const handleTeamSelection = (team: Team) => {
    setSelectedTeam(team);
    toggleModal({ type: ModalType.TeamSelection });
    setShouldValidate(false);
  };

  const handleContinue = async () => {
    if (selectedTeam.id === defaultTeamOption.id) {
      setShouldValidate(true);
      return;
    }

    const userToCreate = {
      roleId,
      emails: [email],
      teamId: selectedTeam.id,
      storeId,
      status: "ACTIVE",
    };

    try {
      const response = await createUserMutation(userToCreate);
      const createdUser = response?.[0];

      if (createdUser?.uid) {
        setLoggedInUser({ ...createdUser, atkomstRoles: [] });
      }

      await queryClient.invalidateQueries({ queryKey: ["user", email, uid] });
      navigate("/home");
    } catch {
      // isCreateUserError from the mutation will show the error state in the UI
    }
  };

  if (!storeId) return null;

  return (
    <div className={styles["new-user-team-page-wrapper"]}>
      <FullScreenPopup
        actionBarContent={
          !isLoading && (
            <FixaButton
              aria-label={t("submitSelectedTeamString")}
              className={styles["action-bar-continue-button"] || ""}
              disabled={!hasTeams || isCreatingUser}
              text={<Trans>continue2String</Trans>}
              type="primary"
              onClick={handleContinue}
            />
          )
        }
        noPadding
        noAppBar
      >
        <SimpleHeader displayAllScreens />
        <div className={styles["header-section"]}>
          <FixaText
            tagName="p"
            textStyle="Body.S"
            className={styles["current-store-label"]}
          >
            {t("adminStoreFilter")}
          </FixaText>
          <FixaText
            tagName="h2"
            textStyle="Heading.L"
            className={styles["store-name"]}
          >
            {`${storeName} - ${storeId}`}
          </FixaText>
        </div>

        {isLoading ? (
          <SimpleLoadingBall text={t("loadingEllipsisString")} size="medium" />
        ) : (
          <div className={styles["form-container"]}>
            {isCreateUserError && (
              <FixaText
                tagName="p"
                textStyle="Body.M"
                className={styles["error-message"]}
              >
                {t("isCreateUserError")}
              </FixaText>
            )}

            <FixaFormField
              id="team-form-field"
              className={styles["team-form-field"] || ""}
              fieldHelper={{
                id: "helper-msg-change-team-later",
                msg: t("changeTeamsLaterString"),
                type: "",
              }}
              valid={false}
              validation={{
                id: "error-msg-id",
                msg: t("selectTeamString"),
                type: "error",
              }}
              disabled={!hasTeams}
              shouldValidate={shouldValidate}
            >
              <FixaInputField
                id="team-input-field"
                aria-label={selectedTeam.name || t("joinTeamString")}
                readOnly
                type="text"
                value={
                  selectedTeam.id === defaultTeamOption.id
                    ? ""
                    : selectedTeam.name
                }
                onClick={() => toggleModal({ type: ModalType.TeamSelection })}
                disabled={!hasTeams}
                ssrIcon={ChevronDownIconPath}
                iconPosition="trailing"
                label={t("joinTeamString")}
                iconOnClick={() =>
                  toggleModal({ type: ModalType.TeamSelection })
                }
              />
            </FixaFormField>

            <TeamsModal
              handleTeamSelection={handleTeamSelection}
              activeTeams={activeTeams}
              selectedTeamId={selectedTeam?.id}
            />

            {!hasTeams && (
              <div className={styles["no-teams-container"]}>
                <TeamSelectionMessage
                  variant="cautionary"
                  title={t("noTeamsString")}
                  message={t("noTeamsMessageString")}
                  icon={InformationCircleIconPath}
                  showAction={false}
                />
              </div>
            )}

            <div className={styles["super-users-container"]}>
              <TeamSelectionMessage
                variant="informative"
                title={t("missingTeamString")}
                message={t("missingTeamBodyString")}
                icon={InformationCircleIconPath}
                showAction={true}
                actionText={t("listOfSuperUsersString")}
              />
            </div>

            <SuperUsersListModal
              storeId={normalizedStoreId}
              forceAtkomst={true}
            />
          </div>
        )}
      </FullScreenPopup>
    </div>
  );
};
